import { useNavigate, useSearch } from "@tanstack/react-router";

import { hasRole, useCurrentUser } from "../hooks/useAuth.js";
import type { SessionUser } from "../hooks/useAuth.js";

/**
 * „Meine Kunden" (`onlyMine=true`) oder „mein Bereich" — als Wahl in der URL.
 *
 * **Warum in der URL und nicht im Zustand.** Ein Klick auf eine
 * Dashboard-Kachel führt auf die Termine- oder Wartungsseite; die Wahl muss
 * dort ankommen, sonst zählt die Kachel eine andere Menge, als die Seite
 * zeigt. Als Search-Parameter reist sie automatisch mit, überlebt einen
 * Reload und lässt sich verschicken. Gemerkt wird sie bewusst nicht
 * (Entscheidung Hendrik Niechciol, 2026-09-10) — jede Seite startet mit der
 * Voreinstellung.
 *
 * Name und Form folgen `bcg.tsx` (`?onlyMine=true|false`, fehlt = Vorgabe).
 */

export function parseOnlyMine(raw: unknown): boolean | undefined {
  if (raw === true || raw === "true") return true;
  if (raw === false || raw === "false") return false;
  return undefined;
}

/**
 * Voreinstellung: „meine Kunden" für alle, die welche haben.
 *
 * `projektleiter` wird Leads implizit verliehen (`loadUserRoles`) — wer die
 * Rolle nicht trägt, ist nirgends Lead, und „meine Kunden" wäre für ihn eine
 * leere Seite. Für Admin und Geschäftsleitung ohne eigene Kunden bleibt es
 * deshalb beim Bereich.
 */
export function defaultOnlyMine(user: SessionUser | null): boolean {
  return hasRole(user, "projektleiter");
}

/**
 * Ob es überhaupt zwei Sichten gibt. Ein reiner Projektleiter sieht in beiden
 * Stellungen dasselbe; ein Admin ohne Lead-Kunden hätte bei „meine" nichts.
 * Der Umschalter zeigt sich nur, wenn die Wahl etwas ändert.
 */
export function canToggleOnlyMine(user: SessionUser | null): boolean {
  return (
    hasRole(user, "projektleiter") &&
    hasRole(user, ["admin", "geschaeftsleitung", "bereichsleiter", "stellvertreter"])
  );
}

export function useOnlyMine(): {
  onlyMine: boolean;
  setOnlyMine: (onlyMine: boolean) => void;
  umschaltbar: boolean;
} {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { onlyMine?: unknown };
  const onlyMine = parseOnlyMine(search.onlyMine) ?? defaultOnlyMine(user);
  return {
    onlyMine,
    umschaltbar: canToggleOnlyMine(user),
    setOnlyMine: (naechste) => {
      // Auf der aktuellen Route bleiben, nur den einen Parameter ersetzen —
      // ein gesetzter Ampel-Filter soll die Umschaltung überleben.
      void navigate({
        to: ".",
        search: (prev: Record<string, unknown>) => ({ ...prev, onlyMine: naechste }),
      } as never);
    },
  };
}
